import { useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import { db } from "../FIREBASE/firebase";
import routeUrls from "../COMPONENTS/navbar/navLink";
import { usePageBackground } from "../COMPONENTS/usePageBackground";
import {
  Exercise, LiftEntry, newId, todayIso, dayTime, fmtDay, fmtKg,
  sortedEntries, recordIds, platesFor, normalizeLifts, SEED_LIFTS, BAR_KG,
} from "../COMPONENTS/lifts/lifts";
import "../CSS/lifts.scss";

// Weight over time as a plain SVG line chart (no chart library — see CLAUDE.md)
function Chart({ entries }: { entries: LiftEntry[] }) {
  const W = 360, H = 190, L = 36, R = 14, T = 12, B = 26;
  const kgs = entries.map((e) => e.kg);
  let lo = Math.max(0, Math.min(...kgs) - 5), hi = Math.max(...kgs) + 5;
  const step = hi - lo <= 20 ? 5 : hi - lo <= 50 ? 10 : 25;
  lo = Math.floor(lo / step) * step;
  hi = Math.ceil(hi / step) * step;
  if (hi <= lo) hi = lo + step;
  const ts = entries.map((e) => dayTime(e.d));
  const tmin = Math.min(...ts), tmax = Math.max(...ts);
  const x0 = L + 10, x1 = W - R - 10;
  const X = (t: number) => (tmax === tmin ? (x0 + x1) / 2 : x0 + ((t - tmin) / (tmax - tmin)) * (x1 - x0));
  const Y = (v: number) => T + ((hi - v) / (hi - lo)) * (H - T - B);
  const grid: number[] = [];
  for (let v = lo; v <= hi + 1e-9; v += step) grid.push(v);
  const pts = entries.map((e, i) => [X(ts[i]), Y(e.kg)]);
  const last = pts.length - 1;
  return (
    <svg className="lifts__chart" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Vikt över tid">
      {grid.map((v) => (
        <g key={v}>
          <line className="lifts__chart-grid" x1={L} x2={W - R} y1={Y(v)} y2={Y(v)} />
          <text x={L - 6} y={Y(v) + 4} textAnchor="end">{fmtKg(v)}</text>
        </g>
      ))}
      {pts.length > 1 && <polyline className="lifts__chart-line" points={pts.map((p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ")} />}
      {pts.map((p, i) => (
        <circle key={entries[i].id} className={`lifts__chart-pt${i === last ? " lifts__chart-pt--last" : ""}`} cx={p[0]} cy={p[1]} r={i === last ? 5 : 4}>
          <title>{`${fmtDay(entries[i].d, true)}: ${fmtKg(entries[i].kg)} kg`}</title>
        </circle>
      ))}
      {entries.length === 1 ? (
        <text x={pts[0][0]} y={H - 6} textAnchor="middle">{fmtDay(entries[0].d)}</text>
      ) : (
        <>
          <text x={pts[0][0]} y={H - 6} textAnchor="start">{fmtDay(entries[0].d)}</text>
          <text x={pts[last][0]} y={H - 6} textAnchor="end">{fmtDay(entries[last].d)}</text>
        </>
      )}
    </svg>
  );
}

function Plates({ kg }: { kg: number }) {
  const p = platesFor(kg);
  if (!p) return <div className="lifts__plates">Lättare än en stång på {BAR_KG} kg.</div>;
  if (!p.plates.length) return <div className="lifts__plates">Bara stången, {BAR_KG} kg.</div>;
  return (
    <div className="lifts__plates">
      <span>Per sida:</span>
      {p.plates.map((pl, i) => <span key={i} className="lifts__plate" style={{ background: pl.bg, color: pl.fg }}>{fmtKg(pl.kg)}</span>)}
      {p.rest > 0 && <span>Går inte jämnt ut med standardskivor.</span>}
    </div>
  );
}

export const LiftsView = ({ uid, isOwner }: { uid: string; isOwner: boolean }) => {
  usePageBackground();
  const [lifts, setLifts] = useState<Exercise[]>([]);
  const [hydrated, setHydrated] = useState(false);
  const [selId, setSelId] = useState<string | null>(() => { try { return localStorage.getItem("lifts-sel"); } catch { return null; } });
  const [adding, setAdding] = useState(false);
  const [confirmKey, setConfirmKey] = useState<string | null>(null);
  const [kg, setKg] = useState("");
  const [reps, setReps] = useState("");
  const [date, setDate] = useState(todayIso());
  const [newName, setNewName] = useState("");
  const [newBar, setNewBar] = useState(true);
  const [status, setStatus] = useState<{ msg: string; err?: boolean } | null>(null);
  // Delete buttons only show in edit mode, so nothing is removed by a stray tap
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    db.collection("users").doc(uid).get().then((snap) => {
      if (cancelled) return;
      const raw = snap.data()?.lifts;
      // First visit for the owner: bring over the log kept in the Claude artifact
      setLifts(raw === undefined && isOwner ? SEED_LIFTS : normalizeLifts(raw));
      setHydrated(true);
    }).catch(() => { setStatus({ msg: "Kunde inte hämta loggen. Ladda om sidan.", err: true }); });
    return () => { cancelled = true; };
  }, [uid, isOwner]);

  useEffect(() => {
    if (!hydrated) return;
    const t = window.setTimeout(() => {
      db.collection("users").doc(uid).set({ lifts }, { merge: true })
        .catch(() => setStatus({ msg: "Kunde inte spara. Kontrollera anslutningen och försök igen.", err: true }));
    }, 500);
    return () => window.clearTimeout(t);
  }, [lifts, hydrated, uid]);

  const ordered = useMemo(() => [...lifts].sort((a, b) => a.created - b.created), [lifts]);
  const sel = ordered.find((x) => x.id === selId) || ordered[0] || null;
  const entries = sel ? sortedEntries(sel) : [];
  const lastEntry = entries[entries.length - 1];

  // Reset the log form to the latest weight when switching exercise
  useEffect(() => {
    if (!sel) return;
    try { localStorage.setItem("lifts-sel", sel.id); } catch { /* private mode */ }
    const es = sortedEntries(sel);
    setKg(es.length ? String(es[es.length - 1].kg) : "");
    setReps("");
    setDate(todayIso());
    setConfirmKey(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel?.id]);

  const updateSel = (fn: (x: Exercise) => Exercise) => setLifts((cur) => cur.map((x) => (x.id === sel?.id ? fn(x) : x)));

  const step = (delta: number) => setKg((v) => String(Math.max(0, Math.round(((parseFloat(v.replace(",", ".")) || 0) + delta) * 100) / 100)));

  const logEntry = (e: React.FormEvent) => {
    e.preventDefault();
    const n = parseFloat(kg.replace(",", "."));
    if (!sel || !isFinite(n) || n < 0) { setStatus({ msg: "Fyll i en vikt. 0 kg går bra för kroppsvikt.", err: true }); return; }
    const entry: LiftEntry = { id: newId(), d: date || todayIso(), kg: n };
    const r = parseInt(reps, 10);
    if (r > 0) entry.reps = r;
    updateSel((x) => ({ ...x, entries: [...x.entries, entry] }));
    setReps("");
    setStatus({ msg: `Loggat: ${fmtKg(n)} kg.` });
  };

  const addExercise = (e: React.FormEvent) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    const ex: Exercise = { id: newId(), name, bar: newBar, created: Date.now(), entries: [] };
    setLifts((cur) => [...cur, ex]);
    setSelId(ex.id);
    setNewName("");
    setAdding(false);
    setStatus(null);
  };

  // Two-step delete: first tap arms the button, second confirms
  const confirm = (key: string, action: () => void) => {
    if (confirmKey === key) { setConfirmKey(null); action(); } else setConfirmKey(key);
  };

  const records = recordIds(entries);
  const best = entries.length ? Math.max(...entries.map((e) => e.kg)) : 0;
  const prev = entries.length > 1 ? entries[entries.length - 2] : null;
  const diff = prev && lastEntry ? Math.round((lastEntry.kg - prev.kg) * 100) / 100 : null;
  const showNew = hydrated && (adding || !ordered.length);

  return (
    <div className="lifts">
      <header className="lifts__header">
        <NavLink to={routeUrls.PROJEKT} className="lifts__back">← Projekt</NavLink>
        <h1 className="lifts__title">Träningslogg</h1>
        <span className="lifts__sub">Vikt per övning över tid.</span>
      </header>

      <div className="lifts__layout">
        <nav className="lifts__tabs" aria-label="Övningar">
          {ordered.map((x) => (
            <button key={x.id} type="button" className={`lifts__tab${x.id === sel?.id && !adding ? " lifts__tab--on" : ""}`}
              aria-pressed={x.id === sel?.id && !adding} onClick={() => { setSelId(x.id); setAdding(false); setStatus(null); }}>
              {x.name}
            </button>
          ))}
          {hydrated && <button type="button" className="lifts__tab lifts__tab--ghost" onClick={() => setAdding((a) => !a)}>{adding ? "Avbryt" : "+ Ny övning"}</button>}
        </nav>

        <div className="lifts__main">
          {showNew && (
            <section className="lifts__card">
              <h2 className="lifts__label">Ny övning</h2>
              <form className="lifts__form" onSubmit={addExercise} autoComplete="off">
                <label className="lifts__field"><span>Namn</span>
                  <input className="lifts__input" type="text" maxLength={60} required placeholder="Till exempel Bänkpress" value={newName} onChange={(e) => setNewName(e.target.value)} autoFocus={adding} /></label>
                <label className="lifts__check">
                  <input type="checkbox" checked={newBar} onChange={(e) => setNewBar(e.target.checked)} />
                  Skivstång (visar skivor per sida, stång {BAR_KG} kg)
                </label>
                <button className="lifts__primary" type="submit">Lägg till övning</button>
              </form>
            </section>
          )}

          {hydrated && sel && !adding && (
            <section className="lifts__card">
              <h2 className="lifts__label">Logga {sel.name.toLowerCase()}</h2>
              <form className="lifts__form" onSubmit={logEntry} autoComplete="off">
                <label className="lifts__field">
                  <span>Vikt (kg)</span>
                  <div className="lifts__stepper">
                    <button type="button" onClick={() => step(-2.5)} aria-label="Minska 2,5 kg">−2,5</button>
                    <input type="number" inputMode="decimal" step="any" min="0" required value={kg} onChange={(e) => setKg(e.target.value)} />
                    <button type="button" onClick={() => step(2.5)} aria-label="Öka 2,5 kg">+2,5</button>
                  </div>
                </label>
                <div className="lifts__row">
                  <label className="lifts__field"><span>Reps (valfritt)</span>
                    <input className="lifts__input" type="number" inputMode="numeric" min="1" step="1" value={reps} onChange={(e) => setReps(e.target.value)} /></label>
                  <label className="lifts__field"><span>Datum</span>
                    <input className="lifts__input" type="date" required max={todayIso()} value={date} onChange={(e) => setDate(e.target.value)} /></label>
                </div>
                <button className="lifts__primary" type="submit">Logga</button>
              </form>
              {status && <p className={`lifts__status${status.err ? " lifts__status--err" : ""}`} role="status">{status.msg}</p>}
            </section>
          )}

          <section className="lifts__card" aria-live="polite">
            {!hydrated ? (
              <p className="lifts__empty">Hämtar din logg…</p>
            ) : !sel ? (
              <p className="lifts__empty"><b>Ingen övning ännu.</b> Lägg till din första nedan, till exempel benböj, så börjar kurvan ritas efter första passet.</p>
            ) : (
              <>
                <div className="lifts__cardhead">
                  <h2 className="lifts__label">{sel.name}</h2>
                  <button type="button" className={`lifts__edit${editing ? " lifts__edit--on" : ""}`} aria-pressed={editing}
                    onClick={() => { setEditing((v) => !v); setConfirmKey(null); }}>
                    {editing ? "Klar" : "✏️ Redigera"}
                  </button>
                </div>
                {!lastEntry ? (
                  <p className="lifts__empty">Inga pass loggade. Fyll i vikten nedan och tryck Logga.</p>
                ) : (
                  <>
                    <div className="lifts__now">
                      <div className="lifts__big">{fmtKg(lastEntry.kg)}<small>kg</small></div>
                      <div className="lifts__when">{fmtDay(lastEntry.d, true)}{lastEntry.reps ? ` · ${lastEntry.reps} reps` : ""}</div>
                    </div>
                    <div className="lifts__stats">
                      <span className="lifts__stat">Rekord <b>{fmtKg(best)} kg</b></span>
                      {diff !== null && (
                        <span className="lifts__stat">Sedan förra <b className={diff > 0 ? "lifts__up" : diff < 0 ? "lifts__down" : ""}>{diff > 0 ? "+" : ""}{fmtKg(diff)} kg</b></span>
                      )}
                      <span className="lifts__stat">Pass <b>{entries.length}</b></span>
                    </div>
                    {sel.bar && <Plates kg={lastEntry.kg} />}

                    <h2 className="lifts__label lifts__label--section">Utveckling</h2>
                    <Chart entries={entries} />
                    {entries.length === 1 && <p className="lifts__hint">Logga fler pass så ritas kurvan.</p>}

                    <h2 className="lifts__label lifts__label--section">Historik</h2>
                    <ul className="lifts__hist">
                      {[...entries].reverse().map((e) => {
                        const key = `e:${e.id}`;
                        return (
                          <li key={e.id}>
                            <span className="lifts__meta">{fmtDay(e.d, true)}{e.reps ? ` · ${e.reps} reps` : ""}</span>
                            <span className="lifts__kg">{fmtKg(e.kg)} kg{records.has(e.id) && <span className="lifts__tag">Nytt rekord</span>}</span>
                            {editing && (
                              <button type="button" className={`lifts__link${confirmKey === key ? " lifts__link--confirm" : ""}`} aria-label="Ta bort pass"
                                onClick={() => confirm(key, () => updateSel((x) => ({ ...x, entries: x.entries.filter((y) => y.id !== e.id) })))}>
                                {confirmKey === key ? "Bekräfta" : "Ta bort"}
                              </button>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </>
                )}
                {editing && (
                  <div className="lifts__foot">
                    <button type="button" className={`lifts__link${confirmKey === `x:${sel.id}` ? " lifts__link--confirm" : ""}`}
                      onClick={() => confirm(`x:${sel.id}`, () => { setLifts((cur) => cur.filter((x) => x.id !== sel.id)); setSelId(null); setEditing(false); })}>
                      {confirmKey === `x:${sel.id}` ? "Bekräfta borttagning av övningen" : "Ta bort övning"}
                    </button>
                  </div>
                )}
              </>
            )}
          </section>

          {status && !(hydrated && sel && !adding) && <p className={`lifts__status${status.err ? " lifts__status--err" : ""}`} role="status">{status.msg}</p>}
        </div>
      </div>
    </div>
  );
};
